import { hasPermission } from '@helios/authz';
import {
  DASHBOARD_ATTENTION_CATALOG,
  DASHBOARD_ATTENTION_OUTCOMES,
  DASHBOARD_ATTENTION_TONES,
  DASHBOARD_ATTENTION_VERSION_KINDS,
} from '@helios/types';
import { z } from 'zod';
import type { RealtimeEventItem } from '@/services/realtimeClient';

/** One public catalog prevents server/browser permission, text and tone drift. */
export const VERIFIED_CHANGE_CATALOG = DASHBOARD_ATTENTION_CATALOG;
const timestamp = z.string().datetime();
const fingerprint = z.string().regex(/^[a-f0-9]{64}$/);
const innerSchema = z
  .object({
    schemaVersion: z.literal(1),
    outcome: z.enum(DASHBOARD_ATTENTION_OUTCOMES),
    entityVersion: z.string().min(1).max(40),
    evidenceFingerprintSha256: fingerprint,
    text: z.string().min(1).max(100),
  })
  .strict();
const changeSchema = innerSchema
  .extend({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    actorId: z.string().uuid().nullable(),
    recipientUserId: z.string().uuid().nullable(),
    eventName: z.string(),
    topic: z.string(),
    permission: z.string(),
    entityType: z.string(),
    entityId: z.string().uuid(),
    timestamp,
    tone: z.enum(DASHBOARD_ATTENTION_TONES),
    seenAt: timestamp.nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const expected = VERIFIED_CHANGE_CATALOG[value.outcome];
    const kind = DASHBOARD_ATTENTION_VERSION_KINDS[value.outcome];
    const validVersion =
      kind === 'timestamp'
        ? timestamp.safeParse(value.entityVersion).success
        : kind === 'receipt_identity'
          ? z.string().uuid().safeParse(value.entityVersion).success
          : /^[1-9]\d*$/.test(value.entityVersion) &&
            Number.isSafeInteger(Number(value.entityVersion));
    if (
      !validVersion ||
      Object.entries(expected).some(
        ([key, expectedValue]) => value[key as keyof typeof expected] !== expectedValue,
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Outcome does not match its persisted source catalog',
      });
    }
  });
export type VerifiedChange = Readonly<z.infer<typeof changeSchema>>;
export interface VerifiedChangeScope {
  readonly tenantId: string;
  readonly userId: string;
  readonly permissions: readonly string[];
  readonly sessionOrigin: string;
  readonly observerKey?: string;
}
export interface VerifiedChangeEntry {
  readonly change: VerifiedChange;
  readonly pulseStartedAt: number | null;
}
export interface VerifiedChangeRecordIdentity {
  readonly entityType: string;
  readonly entityId: string;
  readonly entityVersion: string;
  readonly evidenceFingerprintSha256: string;
}
export interface VerifiedChangeState {
  readonly scopeKey: string;
  readonly hydrated: boolean;
  readonly readyTimestamp: string | null;
  readonly baselineTimestamp: string | null;
  readonly retiredThroughTimestamp: string | null;
  readonly observedIds: readonly string[];
  readonly entries: readonly VerifiedChangeEntry[];
}
export type VerifiedChangeDelivery = 'hydrate' | 'reconnect' | 'poll' | 'filter' | 'sort' | 'live';
export const VERIFIED_CHANGE_PULSE_MS = 600;
export const VERIFIED_CHANGE_PULSE_COUNT = 2;
export const VERIFIED_CHANGE_ATTENTION_MS = VERIFIED_CHANGE_PULSE_MS * VERIFIED_CHANGE_PULSE_COUNT;
export const VERIFIED_CHANGE_MAX_ENTRIES = 1000;
export const VERIFIED_CHANGE_MAX_OBSERVED_IDS = 2048;

export function readVerifiedChange(value: unknown): VerifiedChange | null {
  const parsed = changeSchema.safeParse(value);
  return parsed.success ? Object.freeze(parsed.data) : null;
}

export function readVerifiedChangeEvent(event: RealtimeEventItem): VerifiedChange | null {
  if (!record(event.payload)) return null;
  const parsed = innerSchema.safeParse(event.payload.dashboardAttention);
  if (
    !parsed.success ||
    (parsed.data.outcome === 'takeoff_run_refused' && event.payload.refused !== true)
  )
    return null;
  const expected = VERIFIED_CHANGE_CATALOG[parsed.data.outcome];
  return readVerifiedChange({
    ...parsed.data,
    id: event.id,
    tenantId: event.tenantId,
    actorId: event.actorId,
    recipientUserId: event.recipientUserId,
    eventName: event.eventName,
    topic: event.topic,
    permission: event.permission,
    entityType: event.entityType,
    entityId: event.entityId,
    timestamp: event.timestamp,
    tone: expected.tone,
    seenAt: null,
  });
}

export function verifiedChangeScopeKey(scope: VerifiedChangeScope): string {
  return JSON.stringify([
    scope.tenantId,
    scope.userId,
    [...new Set(scope.permissions)].sort(),
    scope.sessionOrigin,
    scope.observerKey ?? '',
  ]);
}

export function emptyVerifiedChangeState(scope: VerifiedChangeScope): VerifiedChangeState {
  return Object.freeze({
    scopeKey: verifiedChangeScopeKey(scope),
    hydrated: false,
    readyTimestamp: null,
    baselineTimestamp: null,
    retiredThroughTimestamp: null,
    observedIds: Object.freeze([]),
    entries: Object.freeze([]),
  });
}

/** Only the authenticated gateway's server timestamp arms a live presentation. */
export function readyVerifiedChanges(
  state: VerifiedChangeState,
  scope: VerifiedChangeScope,
  serverTimestamp: string,
): VerifiedChangeState {
  const current =
    state.scopeKey === verifiedChangeScopeKey(scope) ? state : emptyVerifiedChangeState(scope);
  if (!timestamp.safeParse(serverTimestamp).success) return current;
  return Object.freeze({
    ...current,
    readyTimestamp: latestTimestamp(current.readyTimestamp, serverTimestamp),
    entries: Object.freeze(
      current.entries.map((entry) => Object.freeze({ ...entry, pulseStartedAt: null })),
    ),
  });
}

/** Quiet hydration/poll/reconnect/filter/sort never infers changes from numerical differences. */
export function receiveVerifiedChanges(
  state: VerifiedChangeState,
  scope: VerifiedChangeScope,
  values: readonly unknown[],
  delivery: VerifiedChangeDelivery,
  serverTimestamp: string | null,
  monotonicNow: number,
  reducedMotion = false,
  socketReady = false,
): VerifiedChangeState {
  const current =
    state.scopeKey === verifiedChangeScopeKey(scope) ? state : emptyVerifiedChangeState(scope);
  if (!Number.isFinite(monotonicNow) || monotonicNow < 0) return current;
  const observed = new Set(current.observedIds);
  // A completed current-source poll removes prior inaccessible/absent rows.
  // Preserve only genuinely newer live arrivals than that fixed API snapshot.
  const replace =
    (delivery === 'hydrate' || delivery === 'poll') &&
    serverTimestamp &&
    timestamp.safeParse(serverTimestamp).success;
  const previousEntries = new Map(current.entries.map((entry) => [entry.change.id, entry]));
  const entries = new Map(
    current.entries
      .filter(
        (entry) => !replace || Date.parse(entry.change.timestamp) > Date.parse(serverTimestamp!),
      )
      .map((entry) => [entry.change.id, entry]),
  );
  const baseline = latestTimestamp(current.baselineTimestamp, current.readyTimestamp);
  for (const value of values) {
    const change = readVerifiedChange(value);
    if (!change || !canObserve(scope, change)) continue;
    const previous = entries.get(change.id) ?? previousEntries.get(change.id);
    if (previous && !sameEvidence(previous.change, change)) continue;
    const pulse =
      delivery === 'live' &&
      current.hydrated &&
      current.readyTimestamp !== null &&
      socketReady &&
      baseline !== null &&
      Date.parse(change.timestamp) > Date.parse(baseline) &&
      (current.retiredThroughTimestamp === null ||
        Date.parse(change.timestamp) > Date.parse(current.retiredThroughTimestamp)) &&
      !observed.has(change.id) &&
      change.seenAt === null &&
      !reducedMotion;
    const seenAt = change.seenAt ?? previous?.change.seenAt ?? null;
    entries.set(
      change.id,
      Object.freeze({
        change: Object.freeze({ ...change, seenAt }),
        pulseStartedAt: previous?.pulseStartedAt ?? (pulse ? monotonicNow : null),
      }),
    );
    observed.add(change.id);
  }
  const measuredTimestamp =
    serverTimestamp && timestamp.safeParse(serverTimestamp).success ? serverTimestamp : null;
  const sorted = [...entries.values()].sort(
    (a, b) =>
      b.change.timestamp.localeCompare(a.change.timestamp) ||
      b.change.id.localeCompare(a.change.id),
  );
  const retired = sorted
    .slice(VERIFIED_CHANGE_MAX_ENTRIES)
    .reduce(
      (latest, entry) => latestTimestamp(latest, entry.change.timestamp),
      current.retiredThroughTimestamp,
    );
  return Object.freeze({
    ...current,
    hydrated: current.hydrated || (delivery !== 'live' && measuredTimestamp !== null),
    baselineTimestamp: latestTimestamp(current.baselineTimestamp, measuredTimestamp),
    retiredThroughTimestamp: retired,
    observedIds: Object.freeze([...observed].slice(-VERIFIED_CHANGE_MAX_OBSERVED_IDS)),
    entries: Object.freeze(sorted.slice(0, VERIFIED_CHANGE_MAX_ENTRIES)),
  });
}

export function verifiedChangePresentation(
  entry: VerifiedChangeEntry,
  monotonicNow: number,
  reducedMotion = false,
) {
  const elapsed =
    entry.pulseStartedAt === null
      ? VERIFIED_CHANGE_ATTENTION_MS
      : monotonicNow - entry.pulseStartedAt;
  const pulsing =
    !reducedMotion &&
    entry.change.seenAt === null &&
    elapsed >= 0 &&
    elapsed < VERIFIED_CHANGE_ATTENTION_MS;
  return {
    ...entry,
    unseen: entry.change.seenAt === null,
    pulsing,
    pulseElapsedMs: Math.max(0, elapsed),
  };
}

/** An event about a committed revision must never color an older draft or a different source packet. */
export function verifiedChangeMatchesRecord(
  change: VerifiedChange,
  record: VerifiedChangeRecordIdentity,
): boolean {
  return (
    change.entityType === record.entityType &&
    change.entityId === record.entityId &&
    change.entityVersion === record.entityVersion &&
    change.evidenceFingerprintSha256 === record.evidenceFingerprintSha256
  );
}

const receiptSchema = z
  .object({
    eventId: z.string().uuid(),
    tenantId: z.string().uuid(),
    userId: z.string().uuid(),
    entityVersion: z.string(),
    evidenceFingerprintSha256: fingerprint,
    seenAt: timestamp,
  })
  .strict();
export type VerifiedChangeSeenReceipt = Readonly<z.infer<typeof receiptSchema>>;
export function readVerifiedChangeSeenReceipt(
  value: unknown,
  scope: VerifiedChangeScope,
  change: VerifiedChange,
): VerifiedChangeSeenReceipt | null {
  const parsed = receiptSchema.safeParse(value);
  if (!parsed.success || !canObserve(scope, change)) return null;
  const receipt = parsed.data;
  return receipt.tenantId === scope.tenantId &&
    receipt.userId === scope.userId &&
    receipt.eventId === change.id &&
    receipt.entityVersion === change.entityVersion &&
    receipt.evidenceFingerprintSha256 === change.evidenceFingerprintSha256
    ? Object.freeze(receipt)
    : null;
}
export function applyVerifiedChangeSeenReceipt(
  state: VerifiedChangeState,
  scope: VerifiedChangeScope,
  value: unknown,
): VerifiedChangeState {
  if (state.scopeKey !== verifiedChangeScopeKey(scope)) return emptyVerifiedChangeState(scope);
  return Object.freeze({
    ...state,
    entries: Object.freeze(
      state.entries.map((entry) => {
        const receipt = readVerifiedChangeSeenReceipt(value, scope, entry.change);
        return receipt
          ? Object.freeze({
              change: Object.freeze({ ...entry.change, seenAt: receipt.seenAt }),
              pulseStartedAt: null,
            })
          : entry;
      }),
    ),
  });
}

function canObserve(scope: VerifiedChangeScope, change: VerifiedChange): boolean {
  return (
    change.tenantId === scope.tenantId &&
    (change.recipientUserId === null || change.recipientUserId === scope.userId) &&
    hasPermission(scope.permissions, change.permission)
  );
}
function sameEvidence(left: VerifiedChange, right: VerifiedChange): boolean {
  return (
    left.tenantId === right.tenantId &&
    left.actorId === right.actorId &&
    left.entityId === right.entityId &&
    left.entityType === right.entityType &&
    left.entityVersion === right.entityVersion &&
    left.evidenceFingerprintSha256 === right.evidenceFingerprintSha256 &&
    left.outcome === right.outcome &&
    left.timestamp === right.timestamp &&
    left.recipientUserId === right.recipientUserId
  );
}
function latestTimestamp(left: string | null, right: string | null): string | null {
  if (!left) return right;
  if (!right) return left;
  return Date.parse(left) >= Date.parse(right) ? left : right;
}
function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
